import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Loader from "../child/Loder";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";

import {
  fetchDiscoveryConfigById,
  createDiscoveryConfig,
  updateDiscoveryConfig,
} from "../services/discoveryConfigService";

const DiscoveryConfigFormPage = () => {
  const { id } = useParams(); // if exists => edit mode
  const navigate = useNavigate();

  // The three ranking controls the server reads but the form never showed
  // (founder 2026-09-22). Defaults are what the scorer used all along, so
  // saving without touching them changes nothing. Daily Question stays 0
  // (off) until someone sets it on purpose.
  const TUNING_DEFAULTS = {
    dailyAnswers: 0,
    ageProximity: { close: 10, near: 5 },
    liveness: { unreachable: 8, absent: 8 },
  };
  // Same bounds the API enforces (admin.discoveryConfig): a number from 0
  // to 1000. There is no must-total-100 rule any more (founder decision
  // 2026-09-22): these are relative weights, caps and penalties.
  const MAX_WEIGHT = 1000;

  const [formData, setFormData] = useState({
    name: "",
    weights: {
      dynamicAnswers: "",
      gatekeeper: "",
      profileSteps: "",
      photos: "",
      connectionIntent: "",
      // Vault alignment term (2026-08-12 build; server default 0 = off).
      vault: "",
      distance: {
        near: "",
        medium: "",
        far: "",
        veryFar: "",
      },
      lastSeen: {
        day: "",
        threeDays: "",
        week: "",
      },
      ...TUNING_DEFAULTS,
    },
    minDisplayPercent: 75,
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // Fetch config if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      fetchDiscoveryConfigById(id)
        .then((res) => {
          if (res?.data) {
            // Older configs predate minDisplayPercent and may still carry
            // the retired "boosted" weight: default the one, drop the other.
            // The ranking controls fall back to the scorer's own defaults.
            const { boosted, ...weights } = res.data.weights || {};
            setFormData({
              ...res.data,
              weights: {
                vault: 0,
                ...TUNING_DEFAULTS,
                ...weights,
                ageProximity: { ...TUNING_DEFAULTS.ageProximity, ...(weights.ageProximity || {}) },
                liveness: { ...TUNING_DEFAULTS.liveness, ...(weights.liveness || {}) },
              },
              minDisplayPercent: res.data.minDisplayPercent ?? 75,
            });
          }
        })
        .catch((err) => {
          setErrorMessage(err.message || "Failed to load config");
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  // handle simple change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // handle nested weight change
  const handleWeightChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      let ref = updated.weights;
      const keys = path.split(".");
      keys.slice(0, -1).forEach((k) => {
        ref = ref[k];
      });
      ref[keys[keys.length - 1]] = Number(value);
      return updated;
    });
  };

  // Every weight a finite number from 0 to MAX_WEIGHT, mirroring the API's
  // own check so the form never sends what the server would refuse.
  const invalidWeight = (weights) => {
    for (const [key, value] of Object.entries(weights)) {
      if (value && typeof value === "object") {
        for (const [sub, v] of Object.entries(value)) {
          const n = Number(v);
          if (v === "" || !Number.isFinite(n) || n < 0 || n > MAX_WEIGHT) return `${key}.${sub}`;
        }
      } else {
        const n = Number(value);
        if (value === "" || !Number.isFinite(n) || n < 0 || n > MAX_WEIGHT) return key;
      }
    }
    return null;
  };

  // submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;

    const bad = invalidWeight(formData.weights);
    if (bad) {
      setErrorMessage(`${bad} must be a number from 0 to ${MAX_WEIGHT}`);
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }
    const floor = Number(formData.minDisplayPercent);
    if (!Number.isInteger(floor) || floor < 0 || floor > 99) {
      setErrorMessage("Minimum Displayed Compatibility must be a whole number from 0 to 99");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      if (id) {
        await updateDiscoveryConfig(id, formData);
        setSuccessMessage("Config updated successfully");
      } else {
        await createDiscoveryConfig(formData);
        setSuccessMessage("Config created successfully");
      }
      setTimeout(() => {
        setSuccessMessage("");
        navigate("/discovery-config");
      }, 1000);
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  const weightLabels = {
    dynamicAnswers: "Matching Preferences",
    gatekeeper: "Gatekeeper Compatibility",
    profileSteps: "Profile Completeness ",
    connectionIntent: "Connection Intent Compatibility",
    photos: "Photos",
    vault: "Vault Alignment (answers you both chose)",
  };

  const distanceThresholds = {
    near: "≤ 5 km",
    medium: "≤ 20 km",
    far: "≤ 50 km",
    veryFar: "> 50 km",
  };

  if (loading) return <Loader />;

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            {successMessage && (
              <SuccessAlert
                message={successMessage}
                onClose={() => setSuccessMessage("")}
              />
            )}
            {errorMessage && (
              <DengerAlert
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}

            <form onSubmit={handleSubmit} className="row gy-3">
              {/* Name */}
              <div className="col-md-6">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Enter Config Name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              {/* isActive */}
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <select
                  name="isActive"
                  className="form-select"
                  value={formData.isActive ? "true" : "false"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.value === "true",
                    }))
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              {/* Distance */}
              <div className="col-12">
                <label className="form-label">Location Weights</label>

                <div className="row">
                  {["near", "medium", "far", "veryFar"].map((d) => (
                    <div className="col-md-3" key={d}>
                      <label className="form-label">
                        {d.charAt(0).toUpperCase() + d.slice(1)} (
                        {distanceThresholds[d]})
                      </label>

                      <input
                        type="number"
                        className="form-control"
                        value={formData.weights.distance[d]}
                        onChange={(e) =>
                          handleWeightChange(`distance.${d}`, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Last Seen */}
              <div className="col-12">
                <label className="form-label">Recent Activity Weights</label>

                <div className="row">
                  {["day", "threeDays", "week"].map((ls) => (
                    <div className="col-md-4" key={ls}>
                      <label className="form-label">{ls}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.weights.lastSeen[ls]}
                        onChange={(e) =>
                          handleWeightChange(`lastSeen.${ls}`, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              {/* Weights Section */}
              <div className="col-12">
                <div className="row">
                  {[
                    "dynamicAnswers",
                    "gatekeeper",
                    "profileSteps",
                    "connectionIntent",
                    "photos",
                    "vault",
                  ].map((key) => (
                    <div className="col-md-4" key={key}>
                      <label className="form-label">{weightLabels[key]}</label>

                      <input
                        type="number"
                        className="form-control"
                        value={formData.weights[key]}
                        onChange={(e) =>
                          handleWeightChange(key, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Daily Question overlap: a matching weight, 0 = off */}
              <div className="col-12">
                <label className="form-label">Daily Question Overlap</label>
                <div className="row">
                  <div className="col-md-4">
                    <input
                      type="number"
                      min="0"
                      max={MAX_WEIGHT}
                      className="form-control"
                      value={formData.weights.dailyAnswers}
                      onChange={(e) => handleWeightChange("dailyAnswers", e.target.value)}
                    />
                    <small className="text-muted">
                      Agreement on Daily Questions both members answered, once
                      they share three or more. Counts in the displayed
                      percentage. 0 keeps it off.
                    </small>
                  </div>
                </div>
              </div>

              {/* Age proximity: ranking only */}
              <div className="col-12">
                <label className="form-label">Age Proximity (ranking only)</label>
                <div className="row">
                  {[
                    ["close", "Close (within 3 years)"],
                    ["near", "Near (within 8 years)"],
                  ].map(([k, label]) => (
                    <div className="col-md-4" key={k}>
                      <label className="form-label">{label}</label>
                      <input
                        type="number"
                        min="0"
                        max={MAX_WEIGHT}
                        className="form-control"
                        value={formData.weights.ageProximity[k]}
                        onChange={(e) => handleWeightChange(`ageProximity.${k}`, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <small className="text-muted">
                  A bonus for closer ages when deciding who is introduced first.
                  Never changes the percentage a member reads, and a wide gap
                  is never penalized.
                </small>
              </div>

              {/* Liveness: ranking penalties */}
              <div className="col-12">
                <label className="form-label">Liveness Penalties (ranking only)</label>
                <div className="row">
                  {[
                    ["unreachable", "No push token on file"],
                    ["absent", "Not seen in 7 days"],
                  ].map(([k, label]) => (
                    <div className="col-md-4" key={k}>
                      <label className="form-label">{label}</label>
                      <input
                        type="number"
                        min="0"
                        max={MAX_WEIGHT}
                        className="form-control"
                        value={formData.weights.liveness[k]}
                        onChange={(e) => handleWeightChange(`liveness.${k}`, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <small className="text-muted">
                  Subtracted from the ranking so members nobody can reach, or
                  who have not been seen in a week, are introduced after
                  everyone else. Never changes the percentage a member reads.
                </small>
              </div>

              {/* Presentation floor: not a weight */}
              <div className="col-md-4">
                <label className="form-label">
                  Minimum Displayed Compatibility (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  className="form-control"
                  value={formData.minDisplayPercent}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minDisplayPercent: Number(e.target.value),
                    }))
                  }
                />
                <small className="text-muted">
                  Every pair is shown at least this percentage. Ranking
                  between members is unaffected. Set 0 to show raw scores.
                </small>
              </div>

              {/* Submit */}
              <div className="col-12 text-end mt-3">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting
                    ? "Saving..."
                    : id
                      ? "Update Config"
                      : "Create Config"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryConfigFormPage;
